<script setup lang="ts">
import type { NuxtError } from '#app'

const props = defineProps<{ error: NuxtError }>()
const localePath = useLocalePath()
const { locales } = useI18n()
const showDetails = Boolean(useRuntimeConfig().public.showErrorDetails)
const is404 = Number(props.error.statusCode ?? props.error.status) === 404

// 错误页在内部请求 /__nuxt_error 上渲染，当前语言会退回默认语言。
// 从失败地址的语言前缀决定跳到哪一个 /404，避免 /zh-CN/不存在 被送到英文 404。
function notFoundPath(failedUrl: unknown) {
  const raw = typeof failedUrl === 'string' ? failedUrl : ''
  let pathname = raw
  try {
    pathname =
      raw.startsWith('http://') || raw.startsWith('https://')
        ? new URL(raw).pathname
        : raw.split(/[?#]/)[0] || '/'
  } catch {
    pathname = '/'
  }
  const head = pathname.split('/').filter(Boolean)[0]
  const matched = locales.value.find(item =>
    typeof item === 'string' ? item === head : item.code === head,
  )
  const code = typeof matched === 'string' ? matched : matched?.code
  return code ? localePath('/404', code) : localePath('/404')
}

// 失败地址在运行时挂在 error.url 上，Nuxt 4.6 的 NuxtError 类型里没有这个字段。
function failedUrl(error: NuxtError) {
  const url = (error as NuxtError & { url?: unknown }).url
  return typeof url === 'string' ? url : ''
}

if (is404) {
  await navigateTo(notFoundPath(failedUrl(props.error)), { replace: true, redirectCode: 302 })
  await clearError()
}

const stack = computed(() => {
  const s = props.error.stack
  return s ? (Array.isArray(s) ? s.join('\n') : String(s)) : ''
})
</script>

<template>
  <div v-if="!is404" class="mx-auto mt-[120px] max-w-3xl px-4 pb-16 text-center">
    <p class="break-words">{{ error.message }}</p>
    <pre
      v-if="showDetails && stack"
      class="mt-8 overflow-auto rounded-lg bg-black/50 p-4 text-left text-xs leading-relaxed whitespace-pre-wrap text-red-200"
      >{{ stack }}</pre>
    <button
      type="button"
      class="mt-6 text-sm opacity-80"
      @click="clearError({ redirect: localePath('/') })"
    >
      {{ $t('common.backHome') }}
    </button>
  </div>
</template>
